import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const STATS = [
  { value: '5,000+', label: 'Transactions Processed', icon: 'ri-check-double-line' },
  { value: '10+ Yrs', label: 'Serving Las Vegas', icon: 'ri-award-line' },
  { value: '30 Min', label: 'Avg. Processing Time', icon: 'ri-speed-line' },
  { value: '4.9★', label: 'Customer Rating', icon: 'ri-star-fill' },
];

const VALUES = [
  { icon: 'ri-time-line', title: 'Save Your Time', desc: 'We believe your time is valuable. Our streamlined process gets you in and out quickly, without the long waits typical of DMV offices.' },
  { icon: 'ri-shield-check-line', title: 'Professional Excellence', desc: 'Our licensed professionals stay current with all Nevada DMV requirements and processes to ensure accurate, compliant service every time.' },
  { icon: 'ri-user-heart-line', title: 'Customer First', desc: 'Every customer deserves personalized attention and clear communication. We explain the process and answer all your questions.' },
];

const BENEFITS = [
  { icon: 'ri-medal-line', title: 'Licensed & Bonded', desc: 'Fully licensed Nevada DMV service provider with comprehensive insurance and bonding for your protection.' },
  { icon: 'ri-team-line', title: 'Experienced Team', desc: 'Our staff has decades of combined experience with Nevada DMV processes and requirements.' },
  { icon: 'ri-map-pin-2-line', title: 'Convenient Location', desc: 'Easy to find location on S Sandhill Rd with ample parking and accessibility features.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Transparent Pricing', desc: "Clear, upfront pricing with no hidden fees. You know exactly what you'll pay before we start." },
  { icon: 'ri-phone-line', title: 'Personal Service', desc: "Direct phone access and one-on-one attention. We're here to help, not rush you through." },
  { icon: 'ri-award-line', title: 'Quality Guarantee', desc: "We stand behind our work. If there's an issue with our service, we'll make it right." },
];

const TEAM = [
  { name: 'Maria Rodriguez', title: 'Owner & Manager', exp: '15+ years DMV experience', desc: 'Licensed Nevada DMV service provider specializing in complex title transfers and registrations.', img: 'https://public.readdy.ai/ai/img_res/686cfdda4475fa6ec831edf75d558082.jpg' },
  { name: 'David Chen', title: 'Senior DMV Specialist', exp: '12+ years DMV experience', desc: 'Expert in vehicle registrations, out-of-state transfers, and fleet services for businesses.', img: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20man%20DMV%20specialist%20in%20business%20shirt%20confident%20expression%20dark%20office%20background%20with%20warm%20gold%20lighting%20professional%20headshot%20portrait%20photography&width=400&height=500&seq=team-david-gold-v2&orientation=portrait' },
  { name: 'Amanda Johnson', title: 'Customer Service Manager', exp: '8+ years DMV experience', desc: 'Specializes in customer relations, appointment scheduling, and document preparation assistance.', img: 'https://readdy.ai/api/search-image?query=Professional%20Caucasian%20woman%20customer%20service%20manager%20warm%20smile%20dark%20office%20background%20with%20gold%20accent%20lighting%20professional%20headshot%20portrait%20photography&width=400&height=500&seq=team-amanda-gold-v2&orientation=portrait' },
];

const CERTS = [
  { icon: 'ri-government-line', title: 'Nevada DMV Licensed', desc: 'Official Nevada Department of Motor Vehicles service provider license.' },
  { icon: 'ri-shield-star-line', title: 'Bonded & Insured', desc: 'Comprehensive business insurance and surety bonding for customer protection.' },
  { icon: 'ri-quill-pen-line', title: 'Notary Public', desc: 'Licensed Nevada notary public services for document authentication.' },
  { icon: 'ri-building-line', title: 'State of Nevada', desc: 'Registered Nevada business in good standing with all requirements met.' },
];

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'About Speedy DMV Services Las Vegas - Licensed Nevada DMV Provider',
    description: 'Learn about Speedy DMV Services, your trusted Nevada DMV service provider in Las Vegas. Licensed, bonded, and dedicated to saving you time with professional vehicle registration and title services.',
    keywords: 'about Speedy DMV, Las Vegas DMV services, licensed DMV provider Nevada, professional DMV services',
    canonical: `${siteUrl}/about`,
    schema: { "@context": "https://schema.org", "@graph": [generateLocalBusinessSchema(), generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'About', url: '/about' }])] }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* ── HERO ── */}
      <section className="relative pt-[104px]">
        <div className="relative h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20professional%20office%20interior%20dark%20elegant%20gold%20accents%20modern%20business%20environment%20premium%20corporate%20setting%20warm%20ambient%20lighting%20dark%20walls%20gold%20trim%20minimalist%20design&width=1920&height=600&seq=about-hero-gold-v2&orientation=landscape"
            alt="About Speedy DMV Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/82"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">About Us</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Licensed 3rd Party Nevada DMV Provider</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              About <span className="text-[#F5A623]">Speedy DMV Services</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl">
              Your trusted Nevada DMV service provider in Las Vegas — licensed, bonded, and dedicated to saving you time since 2015.
            </p>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {STATS.map((s, i) => (
              <div key={i} className="py-5 px-6 text-center">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className={`${s.icon} text-[#F5A623] text-xl`}></i>
                </div>
                <p className="text-[#F5A623] text-2xl font-bold">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── COMPANY OVERVIEW ── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
                Nevada&apos;s Trusted DMV Experts
              </h2>
              <p className="text-gray-400 text-sm mb-6 pl-5">Professional DMV Services Since 2015</p>
              <div className="pl-5 space-y-4 text-gray-600 leading-relaxed text-sm">
                <p>
                  Speedy DMV Services has been serving the Las Vegas community for nearly a decade, providing fast and reliable Nevada Department of Motor Vehicles services. We understand that dealing with vehicle registration, title transfers, and DMV paperwork can be time-consuming and confusing.
                </p>
                <p>
                  That&apos;s why we&apos;ve built our business around one simple goal: making your DMV experience as quick and painless as possible. Our licensed professionals handle all the paperwork while you wait, so you can get back to your day without the hassle of long DMV lines.
                </p>
                <p>
                  As a <strong className="text-[#003366]">licensed, independent 3rd party Nevada DMV service provider</strong>, we are not affiliated with the government DMV. We are a private business that assists clients with DMV transactions for a service fee — giving you expert help and personalized service you won&apos;t find at a government office.
                </p>
              </div>
              <div className="pl-5 mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/services" className="flex items-center justify-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-6 py-3 rounded font-bold text-sm transition-colors whitespace-nowrap">
                  <i className="ri-layout-grid-line"></i>
                  <span>View Our Services</span>
                </Link>
                <a href="tel:7029797202" className="flex items-center justify-center space-x-2 border border-[#003366] text-[#003366] hover:bg-blue-50 px-6 py-3 rounded font-semibold text-sm transition-colors whitespace-nowrap">
                  <i className="fas fa-phone text-xs"></i>
                  <span>702.979.7202</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-lg overflow-hidden h-[400px] border border-gray-200">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20DMV%20service%20office%20interior%20dark%20elegant%20modern%20design%20customer%20service%20counter%20gold%20accents%20premium%20business%20environment%20Las%20Vegas%20Nevada&width=700&height=500&seq=about-office-gold-v2&orientation=landscape"
                  alt="Speedy DMV Services Office"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#003366]/80 via-transparent to-transparent flex items-end p-6">
                  <div>
                    <p className="text-[#F5A623] text-xs font-bold uppercase tracking-widest mb-1">Our Las Vegas Office</p>
                    <p className="text-white font-bold text-lg">4525 S Sandhill Rd, Las Vegas, NV</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#F5A623] rounded-lg p-5 text-white shadow-lg">
                <p className="font-bold text-2xl leading-none">30 Min</p>
                <p className="text-xs font-semibold mt-1 text-white/80">Avg. Processing</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION & VALUES ── */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Our Mission &amp; Values
          </h2>
          <p className="text-gray-500 mb-10 pl-5">What drives us every day.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <div key={i} className="group bg-white border border-gray-200 hover:border-[#003366] rounded-lg p-7 text-center transition-all hover:shadow-md">
                <div className="w-14 h-14 flex items-center justify-center bg-[#003366] rounded-full mx-auto mb-5 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${v.icon} text-[#F5A623] group-hover:text-white text-2xl transition-colors`}></i>
                </div>
                <h3 className="font-bold text-[#003366] text-base mb-3">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Why Choose Speedy DMV Services
          </h2>
          <p className="text-gray-500 mb-10 pl-5">The trusted choice for Las Vegas drivers.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="group flex items-start space-x-4 p-5 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50/30 transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#003366] rounded flex-shrink-0 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${b.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#003366] text-sm mb-1">{b.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Meet Our Professional Team
          </h2>
          <p className="text-gray-500 mb-10 pl-5">Experienced, licensed, and dedicated to your success.</p>
          <div className="grid md:grid-cols-3 gap-7">
            {TEAM.map((m, i) => (
              <div key={i} className="group bg-white border border-gray-200 hover:border-[#003366] rounded-lg overflow-hidden transition-all hover:shadow-md">
                <div className="h-60 overflow-hidden">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 border-t-2 border-[#F5A623]">
                  <h3 className="font-bold text-[#003366] text-base mb-0.5">{m.name}</h3>
                  <p className="text-[#F5A623] text-sm font-semibold mb-1">{m.title}</p>
                  <p className="text-gray-400 text-xs mb-3 flex items-center space-x-1">
                    <i className="ri-award-line"></i>
                    <span>{m.exp}</span>
                  </p>
                  <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CERTIFICATIONS ── */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-2 border-l-4 border-[#F5A623] pl-4">
            Licensed &amp; Certified
          </h2>
          <p className="text-white/60 mb-10 pl-5">Our credentials and qualifications.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {CERTS.map((c, i) => (
              <div key={i} className="group text-center bg-white/10 border border-white/20 hover:bg-white/15 rounded-lg p-7 transition-all">
                <div className="w-14 h-14 flex items-center justify-center bg-[#F5A623] rounded-full mx-auto mb-4">
                  <i className={`${c.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="font-bold text-white text-sm mb-2">{c.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#003366] mb-3">
            Ready to Experience the <span className="text-[#F5A623]">Difference?</span>
          </h2>
          <p className="text-gray-500 mb-8">Join thousands of satisfied Las Vegas customers who skip the DMV lines.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-8 py-4 rounded font-bold text-base transition-colors whitespace-nowrap">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 rounded font-bold text-base transition-colors whitespace-nowrap">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
